<template>
  <div>
    <!-- Connection card -->
    <v-card elevation="4">
      <v-card-text class="d-flex justify-space-between text--gray-darken2 v-card--mobile">
        <div>
          <v-skeleton-loader type="heading" class="title-skeleton" />
          <v-skeleton-loader type="text" class="subtitle-skeleton mt-1" />
        </div>
        <div class="d-flex align-center">
          <v-skeleton-loader class="button" type="heading" />
        </div>
      </v-card-text>

      <v-divider class="mt-5" />

      <v-card-text>
        <template v-if="connected">
          <div class="d-flex flex-wrap align-center">
            <v-skeleton-loader type="text" class="kv-label mr-2" />
            <v-skeleton-loader type="text" class="kv-value-inline" />
          </div>
        </template>
        <template v-else>
          <div class="d-flex mb-4">
            <v-skeleton-loader class="toggle-btn mr-2" type="heading" />
            <v-skeleton-loader class="toggle-btn" type="heading" />
          </div>
          <v-skeleton-loader class="field mb-2" type="heading" />
          <div class="d-flex align-center justify-space-between mb-2">
            <v-skeleton-loader type="text" class="kv-label" />
            <v-skeleton-loader class="switch" type="heading" />
          </div>
        </template>
      </v-card-text>
    </v-card>

    <template v-if="connected">
      <!-- Branch card -->
      <v-card elevation="4" class="mt-4">
        <v-card-text class="d-flex justify-space-between text--gray-darken2 v-card--mobile">
          <div>
            <v-skeleton-loader type="heading" class="title-skeleton" />
            <v-skeleton-loader type="text" class="subtitle-skeleton mt-1" />
          </div>
          <div class="d-flex align-center">
            <v-skeleton-loader type="text" class="status-text mx-4" />
            <v-skeleton-loader class="button button--text" type="heading" />
          </div>
        </v-card-text>
      </v-card>

      <!-- Last Deployed Commit card -->
      <v-card elevation="4" class="mt-4">
        <v-card-text class="d-flex justify-space-between text--gray-darken2 v-card--mobile">
          <div>
            <v-skeleton-loader type="heading" class="title-skeleton" />
            <v-skeleton-loader type="text" class="subtitle-skeleton mt-1" />
          </div>
          <div class="d-flex align-center">
            <v-skeleton-loader class="button button--text" type="heading" />
          </div>
        </v-card-text>

        <v-divider class="mt-5" />

        <v-card-text class="git-commit-details">
          <v-row
            v-for="row in 3"
            :key="'commit-' + row"
            dense
            class="git-kv-row"
          >
            <v-col cols="12" sm="3">
              <v-skeleton-loader type="text" class="kv-label" />
            </v-col>
            <v-col cols="12" sm="9">
              <v-skeleton-loader type="text" class="kv-value" />
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Auto Deploy card -->
      <v-card elevation="4" class="mt-4">
        <v-card-text class="text--gray-darken2">
          <div class="d-flex justify-space-between flex-wrap v-card--mobile">
            <div class="mb-2 mb-sm-0">
              <v-skeleton-loader type="heading" class="title-skeleton" />
              <v-skeleton-loader type="text" class="subtitle-skeleton mt-1" />
            </div>
            <div class="d-flex align-center">
              <v-skeleton-loader class="switch" type="heading" />
            </div>
          </div>
        </v-card-text>
      </v-card>

      <!-- Pull card -->
      <v-card elevation="4" class="mt-4">
        <v-card-text class="d-flex justify-space-between text--gray-darken2 v-card--mobile">
          <div>
            <v-skeleton-loader type="heading" class="title-skeleton" />
            <v-skeleton-loader type="text" class="subtitle-skeleton mt-1" />
          </div>
          <div class="d-flex align-center">
            <v-skeleton-loader class="button button--primary" type="heading" />
          </div>
        </v-card-text>
      </v-card>

      <!-- Push card -->
      <v-card elevation="4" class="mt-4">
        <v-card-text class="d-flex justify-space-between text--gray-darken2 v-card--mobile">
          <div>
            <v-skeleton-loader type="heading" class="title-skeleton" />
            <v-skeleton-loader type="text" class="subtitle-skeleton mt-1" />
          </div>
          <div class="d-flex align-center">
            <v-skeleton-loader class="button button--primary" type="heading" />
          </div>
        </v-card-text>
      </v-card>

      <!-- Logs card -->
      <v-card elevation="4" class="mt-4">
        <v-card-text class="d-flex justify-space-between text--gray-darken2 v-card--mobile">
          <div>
            <v-skeleton-loader type="heading" class="title-skeleton" />
            <v-skeleton-loader type="text" class="subtitle-skeleton mt-1" />
          </div>
        </v-card-text>

        <v-divider class="mt-5" />

        <v-card-text>
          <div
            v-for="row in 5"
            :key="'log-' + row"
            class="logs-row d-flex"
          >
            <v-skeleton-loader
              v-for="col in 4"
              :key="'log-' + row + '-' + col"
              type="text"
              class="logs-cell"
            />
          </div>
        </v-card-text>
      </v-card>
    </template>
  </div>
</template>

<script>
export default {
  name: "GitCardSkeleton",
  props: {
    connected: {
      type: Boolean,
      default: false,
    },
  },
};
</script>

<style lang="scss" scoped>
.v-card--mobile {
  @media (max-width: 767px) {
    flex-direction: column;
    align-items: flex-start;

    > :last-child {
      margin-top: 12px;
      width: 100%;
      justify-content: flex-start;
    }

    .v-btn,
    .button {
      width: 100%;
    }
  }
}

.v-skeleton-loader.title-skeleton::v-deep {
  .v-skeleton-loader__heading {
    height: 16px;
    max-width: 140px;
    width: 100%;
    margin: 0;
  }
}

.v-skeleton-loader.subtitle-skeleton::v-deep {
  .v-skeleton-loader__text {
    height: 10px;
    max-width: 70%;
    width: 100%;
    margin: 0;
  }
}

.v-skeleton-loader.kv-label::v-deep {
  .v-skeleton-loader__text {
    height: 10px;
    max-width: 60px;
    width: 100%;
    margin: 0;
  }
}

.v-skeleton-loader.kv-value::v-deep {
  .v-skeleton-loader__text {
    height: 10px;
    max-width: 55%;
    width: 100%;
    margin: 0;
  }
}

.v-skeleton-loader.kv-value-inline::v-deep {
  .v-skeleton-loader__text {
    height: 10px;
    max-width: 200px;
    width: 100%;
    margin: 0;
  }
}

.v-skeleton-loader.status-text::v-deep {
  .v-skeleton-loader__text {
    height: 10px;
    max-width: 80px;
    width: 100%;
    margin: 0;
  }
}

.v-skeleton-loader.button::v-deep {
  .v-skeleton-loader__heading {
    height: 32px;
    width: 120px;
    border-radius: 6px;
    margin: 0;
  }

  &.button--text .v-skeleton-loader__heading {
    width: 100px;
  }

  &.button--primary .v-skeleton-loader__heading {
    width: 90px;
  }
}

.v-skeleton-loader.switch::v-deep {
  .v-skeleton-loader__heading {
    height: 28px;
    width: 45px;
    border-radius: 100px;
    margin: 0;
  }
}

.v-skeleton-loader.toggle-btn::v-deep {
  .v-skeleton-loader__heading {
    height: 32px;
    width: 120px;
    border-radius: 4px;
    margin: 0;
  }
}

.v-skeleton-loader.field::v-deep {
  .v-skeleton-loader__heading {
    height: 40px;
    width: 100%;
    border-radius: 4px;
    margin: 0;
  }
}

.git-commit-details {
  $git-kv-gap: 4px;

  .git-kv-row + .git-kv-row {
    margin-top: 0;
  }

  .git-kv-row ::v-deep > .col {
    padding-top: $git-kv-gap / 2;
    padding-bottom: $git-kv-gap / 2;
    line-height: 1.35;
    min-height: 0;
  }
}

.logs-row {
  padding: 12px 0;
  border-bottom: 1px solid var(--v-gray-lighten2);

  &:last-child {
    border-bottom: none;
  }
}

.logs-cell {
  flex: 1;
  padding: 0 8px;

  &:first-child {
    flex: 0 0 175px;
    max-width: 175px;
  }

  &:nth-child(2) {
    flex: 0 0 280px;
    max-width: 280px;
  }

  &:nth-child(3) {
    flex: 1;
    max-width: 280px;
  }

  &:nth-child(4) {
    flex: 0 0 120px;
    max-width: 120px;
  }

  &::v-deep .v-skeleton-loader__text {
    height: 8px;
    width: 100%;
    margin: 0;
  }
}
</style>
